import { useMemo } from 'react';
import { useFeaturePermission } from './FeaturePermissionContext';

/**
 * UseScreenFeatures
 *
 * The primary hook for every screen component.
 * Pass the SCREEN's own code (e.g. 'POS_MENU_MANAGEMENT') and it returns
 * a lookup object for all the actions/buttons on that screen —
 * dynamically from the API, with ZERO hardcoded child codes.
 *
 * ─── Usage ────────────────────────────────────────────────────────────────
 *
 *   const actions = UseScreenFeatures('POS_MENU_MANAGEMENT');
 *
 *   actions.canShow('POS_ADD_NEW_DISH')    // true | false
 *   actions.canEdit('POS_ADD_NEW_DISH')    // true | false
 *   actions.list                           // all FlatFeature[] for this screen
 *
 * ─── Why this works without hardcoding child codes ────────────────────────
 *
 * The context already has the parent→children map from the API response.
 * `getScreenActions('POS_MENU_MANAGEMENT')` returns whatever sub-features
 * the API said belong to that screen — subscription A might return 3 actions,
 * subscription B might return 8. The screen doesn't need to know in advance.
 *
 * The ONLY code you write in a screen is the SCREEN'S OWN code (parent).
 * That doesn't change between subscriptions — it's the screen identifier.
 */
export function UseScreenFeatures(screenCode: string) {
  const { getScreenActions } = useFeaturePermission();

  const actions = useMemo(() => getScreenActions(screenCode),
    [screenCode, getScreenActions],
  );

  // Build a Set of enabled codes for O(1) lookup
  const enabledSet = useMemo(() => new Set(actions.filter(a => a.is_enabled).map(a => a.code)), [actions]);
  const editableSet = useMemo(() => new Set(actions.filter(a => a.is_editable).map(a => a.code)), [actions]);

  // console.log('actions:', actions);
  return {
    /** All action features the API returned for this screen */
    list: actions,

    /** Returns true if the feature code is enabled on this screen */
    canShow: (code: string): boolean => enabledSet.has(code),

    /** Returns true if the feature code is editable on this screen */
    canEdit: (code: string): boolean => editableSet.has(code),

    /**
     * Returns true if ANY action matching a given category/keyword is enabled.
     * Useful when you want a generic check: e.g. "is any export feature on?"
     *
     *   actions.hasAny(code => code.includes('EXPORT'))
     */
    hasAny: (predicate: (code: string) => boolean): boolean =>
      actions.some(a => a.is_enabled && predicate(a.code)),
  };
}

/**
 * useNavFeatures
 *
 * Returns all top-level nav items from the API — dynamically.
 * Use this to render the sidebar / top navigation.
 * Items are already filtered: only is_menu:true items are returned.
 *
 * ─── Usage ────────────────────────────────────────────────────────────────
 *
 *   const nav = useNavFeatures();
 *
 *   nav.items.map(item => (
 *     item.is_enabled
 *       ? <NavLink key={item.code} to={routeFor(item.code)}>{item.code}</NavLink>
 *       : null
 *   ))
 */
export function useNavFeatures() {
  const { getNavItems } = useFeaturePermission();
  const items = useMemo(() => getNavItems(), [getNavItems]);
  return { items };
}